import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Alert,
  Tooltip,
  Skeleton,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import customerService from '../../services/customerService';
import PageHeader from '../../components/common/PageHeader';
import PageShell from '../../components/common/PageShell';
import { PaymentStatusBadge, InvoiceStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency } from '../../utils/format';
import { isDueDateDisplayed } from '../../utils/documentDisplay';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: '',        label: 'All Statuses' },
  { value: 'paid',    label: 'Paid'         },
  { value: 'unpaid',  label: 'Unpaid'       },
  { value: 'overdue', label: 'Overdue'      },
  { value: 'partial', label: 'Partial'      },
  { value: 'draft',   label: 'Draft'        },
];

const InvoicesPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const searchDebounceRef = useRef(null);
  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(val), 350);
  }, []);

  /**
   * Keep a ref that always holds the latest filter values so that
   * fetchInvoices can be a stable function (empty useCallback deps).
   *
   * Without this, useCallback recreates fetchInvoices on every filter
   * change, and the useEffect([fetchInvoices]) fires an extra time
   * — most visibly in React StrictMode (development) where effects
   * already run twice, producing 4+ API calls on a single page load.
   */
  const filtersRef = useRef({ search, status, dateRange });
  filtersRef.current = { search, status, dateRange };

  const fetchInvoices = useCallback(async (page = 1, pageSize = 15, signal) => {
    const { search: s, status: st, dateRange: dr } = filtersRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: pageSize,
        ...(s  && { search: s }),
        ...(st && { payment_status: st }),
        ...(dr?.[0] && { date_from: dr[0].format('YYYY-MM-DD') }),
        ...(dr?.[1] && { date_to:   dr[1].format('YYYY-MM-DD') }),
      };
      const result = await customerService.getInvoices(params, signal);
      setData(result.data);
      setPagination((prev) => ({
        ...prev,
        current:  result.current_page,
        total:    result.total,
        pageSize: result.per_page,
      }));
    } catch (err) {
      if (err?.code === 'ERR_CANCELED') return;
      setError('Failed to load invoices. Please try again.');
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, []); // stable — reads live values from filtersRef

  // Re-fetch (reset to page 1) whenever a filter changes.
  // AbortController cleanup cancels in-flight requests on unmount or rapid filter changes,
  // which also resolves React StrictMode's double-effect invocation in development.
  useEffect(() => {
    const controller = new AbortController();
    fetchInvoices(1, 15, controller.signal);
    return () => controller.abort();
  }, [fetchInvoices, search, status, dateRange]);

  const handleDownloadPDF = async (e, publicId, number) => {
    e.stopPropagation();
    setDownloading(publicId);
    try {
      const blob = await customerService.downloadInvoicePDF(publicId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* server PDF not yet available */
    } finally {
      setDownloading(null);
    }
  };

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'sale_number',
      key: 'sale_number',
      render: (val, record) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 600 }}
          onClick={() => navigate(`/invoices/${record.id}`)}
        >
          {val}
        </Button>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date_issued',
      key: 'date_issued',
      render: (val) => (val ? dayjs(val).format('MMM D, YYYY') : '—'),
    },
    {
      title: 'Due Date',
      dataIndex: 'date_due',
      key: 'date_due',
      render: (val, record) => {
        if (!isDueDateDisplayed(record.settings)) return '—';
        if (!val) return '—';
        const overdue = record.status_payment !== 'paid' && dayjs(val).isBefore(dayjs(), 'day');
        return (
          <Text style={{ color: overdue ? 'var(--varexe-danger)' : undefined, fontWeight: overdue ? 600 : 400 }}>
            {dayjs(val).format('MMM D, YYYY')}
            {overdue && <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>(Overdue)</Text>}
          </Text>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount_total',
      key: 'amount_total',
      align: 'right',
      render: (val, record) => <Text strong>{formatCurrency(val, record.currency)}</Text>,
    },
    {
      title: 'Amount Due',
      dataIndex: 'amount_due',
      key: 'amount_due',
      align: 'right',
      render: (val, record) => (val > 0
        ? <Text style={{ color: 'var(--varexe-danger)', fontWeight: 600 }}>{formatCurrency(val, record.currency)}</Text>
        : <Text type="secondary">—</Text>),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space size={4}>
          <InvoiceStatusBadge status={record.status} />
          <PaymentStatusBadge status={record.status_payment} />
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              variant="filled"
              color="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/invoices/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Download PDF">
            <Button
              variant="filled"
              color="primary"
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading === record.id}
              onClick={(e) => handleDownloadPDF(e, record.id, record.sale_number)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowClassName = (record) => {
    const overdue = isDueDateDisplayed(record.settings)
      && record.status_payment !== 'paid'
      && record.date_due
      && dayjs(record.date_due).isBefore(dayjs(), 'day');
    return overdue ? 'row-overdue' : '';
  };

  if (!initialLoadComplete) {
    return (
      <PageShell>
        <PageHeader
          title="Invoices"
          breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Invoices' }]}
        />
        <Card className="shadow-none" variant="borderless" styles={{ body: { padding: 20 } }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Invoices"
        breadcrumbs={[{ label: 'Dashboard', path: '/dashboard' }, { label: 'Invoices' }]}
      />

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16, borderRadius: 6 }}
        />
      )}

      <Card
        className="shadow-none"
        variant="borderless"
        styles={{ body: { padding: 0 } }}
      >
        <Row
          gutter={[12, 12]}
          align="middle"
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--varexe-border-color)',
            background: 'var(--varexe-white)',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Col xs={24} sm={10} md={8}>
            <Input
              prefix={<SearchOutlined style={{ color: 'var(--varexe-gray-400)' }} />}
              placeholder="Search by invoice number…"
              value={searchInput}
              onChange={handleSearchChange}
              allowClear
              onClear={() => { setSearchInput(''); setSearch(''); }}
            />
          </Col>
          <Col xs={24} sm={7} md={5}>
            <Select
              style={{ width: '100%' }}
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
              suffixIcon={<FilterOutlined />}
            />
          </Col>
          <Col xs={24} sm={7} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={setDateRange}
              placeholder={['From date', 'To date']}
            />
          </Col>
          <Col xs={24} md={3} style={{ textAlign: 'right' }}>
            <Button
              variant="filled"
              color="default"
              icon={<ReloadOutlined />}
              onClick={() => { setSearchInput(''); setSearch(''); setStatus(''); setDateRange(null); }}
            >
              Clear
            </Button>
          </Col>
        </Row>

        <style>{`.row-overdue td { background: var(--varexe-soft-danger) !important; } `}</style>
        <Table
          className="listing-table"
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          rowClassName={rowClassName}
          onRow={(record) => ({ onClick: () => navigate(`/invoices/${record.id}`), style: { cursor: 'pointer' } })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `${total} invoice${total !== 1 ? 's' : ''}`,
            onChange: (page, size) => fetchInvoices(page, size),
          }}
          locale={{ emptyText: 'No invoices found' }}
          size="small"
        />
      </Card>
    </PageShell>
  );
};

export default InvoicesPage;
